'use client';

import React from 'react';

import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

export interface ChartCardProps {
  title: string;
  /** Headline figure for the period, rendered in the mono data style. */
  value?: React.ReactNode;
  /** Short qualifier next to the value, e.g. "/day" or "last 30 days". */
  unit?: React.ReactNode;
  /** Controls that belong to this chart (granularity, series filter). */
  action?: React.ReactNode;
  /** Explains what the chart measures. Keep it to one line. */
  description?: React.ReactNode;
  isLoading?: boolean;
  /** True when the request resolved with nothing to plot. */
  isEmpty?: boolean;
  /** Empty-state copy. Say what is missing and what to do next. */
  emptyMessage?: string;
  emptyAction?: React.ReactNode;
  /** Height of the plot area, matched by the loading skeleton. */
  bodyHeight?: number;
  className?: string;
  children: React.ReactNode;
}

/**
 * The house chart shell: bordered card, header with title and headline figure,
 * plot area, and the loading and empty states that go with it. Every chart in
 * the app sits in one of these so density and chrome stay identical.
 */
export default function ChartCard({
  title,
  value,
  unit,
  action,
  description,
  isLoading = false,
  isEmpty = false,
  emptyMessage = 'No data for this period yet.',
  emptyAction,
  bodyHeight = 280,
  className,
  children,
}: ChartCardProps) {
  return (
    <div
      className={cn(
        'flex h-full min-w-0 flex-col overflow-hidden rounded-lg border border-ds-border-subtlest bg-ds-surface-white',
        className
      )}
    >
      <div className="flex items-start justify-between gap-3 border-b border-ds-border-subtlest px-4 py-3">
        <div className="min-w-0">
          <h3 className="ds-text-body-md-semibold text-ds-text-darker">
            {title}
          </h3>
          {isLoading ? (
            <Skeleton className="mt-1 h-6 w-24 bg-ds-gray-subtlest" />
          ) : value !== undefined ? (
            <p className="ds-text-code-lg tabular-nums text-ds-text-darker mt-1">
              {value}
              {unit ? (
                <span className="ds-text-body-xs-medium text-ds-text-subtle ml-1">
                  {unit}
                </span>
              ) : null}
            </p>
          ) : null}
          {description ? (
            <p className="ds-text-body-xs-medium text-ds-text-subtle mt-1">
              {description}
            </p>
          ) : null}
        </div>
        {action ? <div className="shrink-0">{action}</div> : null}
      </div>

      {isLoading ? (
        <div className="flex-1 p-4" style={{ minHeight: bodyHeight }}>
          <Skeleton className="h-full w-full rounded-md bg-ds-gray-subtlest" />
        </div>
      ) : isEmpty ? (
        <div
          className="m-4 flex flex-1 flex-col items-center justify-center gap-2 rounded-lg border border-ds-border-subtlest bg-ds-gray-subtlest/30 px-6 text-center"
          style={{ minHeight: bodyHeight - 32 }}
        >
          <p className="ds-text-body-sm-medium text-ds-text-subtle">
            {emptyMessage}
          </p>
          {emptyAction}
        </div>
      ) : (
        <div className="min-w-0 flex-1 p-4" style={{ minHeight: bodyHeight }}>
          {children}
        </div>
      )}
    </div>
  );
}
